import { OrderStatusModify } from '@ordersnproducts/modules/shared/models/dco/order-status-modify-snapshot.model';
import { InputFormSelector } from '../../dco.component';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { generateAddedValueFormName } from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/form-added-value-name-generator';
import { Store } from '@ngrx/store';
import { State } from '@cemex/cmx-client-layout';
import { getRmsRecipeSpecifications } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { getSlotForFormID } from '@ordersnproducts/modules/shared/reducers';
import * as fromDcoActions from '@ordersnproducts/modules/shared/actions/dco.actions';
import { Country, getAppInjector } from '../../../../../../utils/mix-reference';
import {
  PUMP_PRIMER_MIX,
  PUMP_PRIMER_QUANTITY,
  PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
  PUMP_NONE_CODE,
} from 'app/shared/pumping/pumping.constant';
import { getPumpPrimerState } from '@ordersnproducts/modules/shared/modules/pump-primer/redux/pump-primer.selectors';
import {
  BOOM_PUMP,
  STATIONARY_PUMP,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/constants/pumps';
import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { Slot } from '@ordersnproducts/modules/shared/reducers/dco.reducer';
export class CreateDco {
  orderStatus: OrderStatusModify;
  public ngDestroy = new Subject();
  public openResetWarningModal = false;
  public invalidForm = true;
  public modifyReceptorActive = false;
  public modifyReviewStatus = false;
  public dcoCheckedForQuantityChange = false;
  public wasWarningModalOpened = false;
  public quantityWasIncrement = false;
  public confirmResetDCO = false;
  status: string;
  public injector = getAppInjector();
  public country = Country.getInstance(this.injector);
  public currentCountry: string;
  formSelector: InputFormSelector;
  public oldPod = null;
  fieldNames = [
    SpecFieldNames.QUANTITY,
    SpecFieldNames.PRODUCT_SELECTION,
    SpecFieldNames.CONTRACT_SELECT,
    SpecFieldNames.APPLICATION,
    SpecFieldNames.SLUMP_PRODUCT,
    SpecFieldNames.LOAD_SIZE,
    SpecFieldNames.DISCHARGE_TIME,
    SpecFieldNames.SPACING,
    SpecFieldNames.ADDITIONAL_INFO,
    SpecFieldNames.COLOR_INFO,
    SpecFieldNames.SUMMER_SET,
    generateAddedValueFormName(0),
    generateAddedValueFormName(1),
    generateAddedValueFormName(2),
    generateAddedValueFormName(3),
    SpecFieldNames.ICE_QUANTITY,
    SpecFieldNames.TEMPERATURE_UNDER,
    SpecFieldNames.POINT_OF_DELIVERY,
    SpecFieldNames.UNLOAD_TYPE,
    PUMP_PRIMER_MIX,
    PUMP_PRIMER_QUANTITY,
    PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
    SpecFieldNames.CLEAN_UP,
    SpecFieldNames.ADJUSTMENT,
    SpecFieldNames.PUMP_LENGTH,
    SpecFieldNames.PUMP,
  ];
  private countriesWithRMXULEnabled = [COUNTRY_CODE.MEXICO] as string[];
  public isModalOpen$: BehaviorSubject<{ isOpen: boolean; specField: SpecFieldNames }> = new BehaviorSubject({
    specField: null,
    isOpen: false,
  });
  constructor(
    protected store: Store<State>,
    public featureToggle: FeatureToggleService,
    protected didCloseWarningModal$: BehaviorSubject<boolean>,
  ) {}
  onInit() {
    this.monitorRecipeChangesForReset();
    this.country.getCountryCode();
    this.currentCountry = this.country.getCountryCodeProperty();
  }
  public setFormSelector(formSelector: InputFormSelector): void {
    this.formSelector = formSelector;
  }
  public getFields(): string[] {
    return this.fieldNames;
  }

  /**
   * The method monitors recipe changes and displays a warning modal if the user attempts
   * to reset the form with unsaved changes.
   */
  public monitorRecipeChangesForReset() {
    this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        debounceTime(1000),
        takeUntil(this.ngDestroy),
        filter(
          (recipes) =>
            recipes.find((recipe) => recipe.id === this.formSelector.formId && this.formSelector.formIndex === 0) !=
            null,
        ),
        map((recipes) =>
          recipes.find((recipe) => recipe.id === this.formSelector.formId && this.formSelector.formIndex === 0),
        ),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap((recipe) => {
          return this.store.select(getSlotForFormID(this.formSelector.formId)).pipe(
            filter((slot) => slot && slot.formSnapshot != null),
            map((slot) => ({ recipe, slot })),
            take(1),
          );
        }),
        filter((stateSnapshot) => !!stateSnapshot.recipe && !!stateSnapshot.slot),
        switchMap(({ recipe, slot }) => {
          return this.store.select(getPumpPrimerState).pipe(
            take(1),
            map(({ unloadTypePumpValues }) => ({ unloadTypePumpValues, recipe, slot })),
          );
        }),
        switchMap(({ unloadTypePumpValues, recipe, slot }) => {
          return this.store.select(getRmsRecipeSpecifications).pipe(
            take(1),
            map((recipes) => ({ recipes, unloadTypePumpValues, recipe, slot })),
          );
        }),
        map(({ recipes, unloadTypePumpValues, recipe, slot }) => {
          let isSameValuePumpPrimerMix = true;
          const valuesAreSame = this.fieldNames.every((name) => {
            switch (name) {
              case SpecFieldNames.TEMPERATURE_UNDER:
                return this.resetAvailabilityTemperatureUnder(recipe.values[name], slot.formSnapshot.values[name]);
              case SpecFieldNames.UNLOAD_TYPE:
                return (
                  this.isSameValueUnloadType(
                    recipe.values[name],
                    slot.formSnapshot.values[name],
                    unloadTypePumpValues,
                  ) && this.markAsValidPumpingAsUnloadType(recipe.values[name], slot.formSnapshot.values[name])
                );
              case PUMP_PRIMER_MIX:
                isSameValuePumpPrimerMix = this.isSameValuePumpPrimerMix(
                  recipe.values[name],
                  slot.formSnapshot.values[name],
                );
                return isSameValuePumpPrimerMix;
              case SpecFieldNames.CLEAN_UP:
                return this.markAsValidCleanUpAsRMXUL(recipe, slot);
              case SpecFieldNames.PUMP_LENGTH:
                return this.checkForPumpLength(recipe, slot);
              case SpecFieldNames.PUMP:
                return this.checkForPumpCapacity(recipe, slot);
              default: {
                let isSameValue = true;
                // Handled undefine value in the field in recipe.values and formSnapshot.values
                if (recipe.values[name] && slot.formSnapshot.values[name]) {
                  isSameValue = recipe.values[name] === slot.formSnapshot.values[name];
                }
                return isSameValue;
              }
            }
          });
          if (!isSameValuePumpPrimerMix && recipes.length > 1) {
            // Avoid display reset warning modal when pump primer mix was changed and there is more than 1 product
            // This is because also will be displayed modal refers to delete additional products
            // when is selecting primer mix in first product
            return EMPTY;
          } else if (!valuesAreSame) {
            if (
              this.featureToggle.isFeatureEnabled(Features.IMPROVEMENTS_MODIFY_ORDER_RULES) &&
              !this.wasWarningModalOpened
            ) {
              this.openResetWarningModal = true;
              this.wasWarningModalOpened = false; // Create and repeat the order, resetting the value to show the DCO warning popup.
            } else if (!this.featureToggle.isFeatureEnabled(Features.IMPROVEMENTS_MODIFY_ORDER_RULES)) {
              this.openResetWarningModal = true;
            }
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public orderStatusModify() {
    // Remove order status
    this.store.dispatch(new fromDcoActions.DeleteModifyOrderStatus());
  }
  protected resetAvailabilityTemperatureUnder(newValue, oldValue): boolean {
    if (newValue === 'NONE' && oldValue && oldValue !== 'NONE') {
      return false;
    } else if (newValue !== 'NONE' && oldValue === 'NONE') {
      return false;
    } else if (newValue !== oldValue && oldValue !== undefined) {
      return false;
    }
    return true;
  }
  public validateChangeDateTime(
    slotsApiRequestedDateTimeSnapshot: moment.Moment,
    requestDatetimeMoment: moment.Moment,
  ): boolean {
    if (
      slotsApiRequestedDateTimeSnapshot != null &&
      slotsApiRequestedDateTimeSnapshot.isSame(requestDatetimeMoment, 'minute') === false
    ) {
      return true;
    }
    return false;
  }
  /**
   * This method is used to check if the pump primer mix has been modified and if the user tries to reset the form
   *
   * @param newValue
   * @param oldValue
   * @returns boolean
   *
   * */
  protected isSameValuePumpPrimerMix(newValue: string, oldValue: string): boolean {
    if (newValue !== PUMP_NONE_CODE && !!oldValue && oldValue === PUMP_NONE_CODE) {
      return false;
    } else if (newValue !== oldValue && !!oldValue && oldValue !== PUMP_NONE_CODE && newValue !== PUMP_NONE_CODE) {
      return false;
    }
    return true;
  }
  /**
   * This method is used to check if the NEW selected Unload Type triggers the pump primer
   * flow based on the OLD value that was already selected. Returns false if new availability
   * confirmation is needed, true if no availability re-confirmation is needed.
   *
   * @param newValue
   * @param oldValue
   * @returns boolean
   *
   * */
  protected isSameValueUnloadType(newValue: string, oldValue: string, unloadTypePumpValues: string[]): boolean {
    if (oldValue === newValue) {
      return true;
    }
    if (!!unloadTypePumpValues) {
      if (!unloadTypePumpValues.includes(oldValue) && unloadTypePumpValues.includes(newValue)) {
        return false;
      } else if (unloadTypePumpValues.includes(oldValue) && !unloadTypePumpValues.includes(newValue)) {
        return true;
      } else if (unloadTypePumpValues.includes(newValue) && unloadTypePumpValues.includes(oldValue)) {
        this.didCloseWarningModal$.next(true);
        return false;
      }
    }
    return true;
  }

  /**
   * Check if unload type has been modified and if the user tries to reset the form RMX UL
   *
   * @param newValue current value of the form
   * @param oldValue previous value of the form
   * @returns boolean
   *
   * */
  public markAsValidPumpingAsUnloadType(newValue: string, oldValue: string) {
    const pumping = [BOOM_PUMP, STATIONARY_PUMP];
    return !(pumping.includes(newValue) && !pumping.includes(oldValue) && oldValue !== undefined);
  }
  /**
   * Check if clean up has been modified and if the country has clean up as dco parameter required to verify availability
   *
   * @param recipe current value of the form
   * @param slot previous value of the form
   * @returns boolean
   *
   * */
  private markAsValidCleanUpAsRMXUL(recipe: { values: { [key: string]: any }; id?: number }, slot: Slot): boolean {
    if (this.countriesWithRMXULEnabled.includes(this.currentCountry)) {
      const result = recipe.values[SpecFieldNames.CLEAN_UP] === slot.formSnapshot.values[SpecFieldNames.CLEAN_UP];
      return result;
    }
    return true;
  }
  /**
   * Checks if the pump length in the given recipe matches the pump length in the given slot.
   *
   * @param recipe - The recipe object containing various specification values.
   * @param slot - The slot object containing a form snapshot with various specification values.
   * @returns {boolean} - Returns true if the pump lengths match or if the current country is not in the list of
   * countries with RMXUL enabled.
   */
  private checkForPumpLength(recipe: { values: { [key: string]: any }; id?: number }, slot: Slot): boolean {
    if (this.countriesWithRMXULEnabled.includes(this.currentCountry)) {
      const result = recipe.values[SpecFieldNames.PUMP_LENGTH] === slot.formSnapshot.values[SpecFieldNames.PUMP_LENGTH];
      return result;
    }
    return true;
  }

  /**
   * Checks if the pump capacity in the given recipe matches the pump capacity in the given slot.
   *
   * @param recipe - The recipe object containing various values.
   * @param slot - The slot object containing a form snapshot with various values.
   * @returns {boolean} - Returns true if the pump capacities match or if the current country is not in the list of
   * countries with RMXUL enabled.
   */
  private checkForPumpCapacity(recipe: { values: { [key: string]: any }; id?: number }, slot: Slot): boolean {
    if (this.countriesWithRMXULEnabled.includes(this.currentCountry)) {
      const result = recipe.values[SpecFieldNames.PUMP] === slot.formSnapshot.values[SpecFieldNames.PUMP];
      return result;
    }
    return true;
  }
}
